import { useMemo } from 'react'
import { Select } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { getAllBillingPeriodsOptions } from '@/api/main/@tanstack/react-query.gen'
import type { BillingPeriodResponse } from '@/api/main/types.gen'
import { formatBillingPeriodLabel, sortPeriodsNewestFirst } from './billingPeriods'

export const BillingPeriodSelect = ({
  periods,
  value,
}: {
  /** Already sorted newest first. */
  periods: BillingPeriodResponse[]
  value: number
}) => {
  const { t } = useTranslation('billing')
  const navigate = useNavigate()

  return (
    <Select
      aria-label={t('billingPeriod')}
      placeholder={t('selectPeriod')}
      data={periods.map(period => ({
        value: String(period.id),
        label: formatBillingPeriodLabel(period),
      }))}
      value={String(value)}
      onChange={id => {
        if (id) navigate({ to: '/billing/$billingPeriodId', params: { billingPeriodId: id } })
      }}
      allowDeselect={false}
      searchable
      w={240}
    />
  )
}

/**
 * Period picker that loads the periods itself, for places outside the suspending page
 * (the error page). Renders nothing while loading or if the periods can't be loaded.
 */
export const BillingPeriodSwitcher = ({ value }: { value: number }) => {
  const { data } = useQuery(getAllBillingPeriodsOptions())
  const periods = useMemo(() => (data ? sortPeriodsNewestFirst(data) : []), [data])
  return periods.length > 0 ? <BillingPeriodSelect periods={periods} value={value} /> : null
}

export default BillingPeriodSelect
